import { useState, useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';

const JOIN_MODAL_KEY = 'join_modal_dismissed';
const MODAL_DELAY_MS = 10000; // 10 seconds
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export const useJoinModal = (isAuthenticated: boolean, isHomePage: boolean) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const { logAction } = useAnalytics();

  // Track page visibility for accurate timing
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    // Only show modal if user is anonymous, on home page, and hasn't been shown before
    if (isAuthenticated || !isHomePage || hasShown) {
      return;
    }

    // Check if modal was previously dismissed
    const dismissedData = localStorage.getItem(JOIN_MODAL_KEY);
    if (dismissedData) {
      const { timestamp } = JSON.parse(dismissedData);
      const now = Date.now();
      if (now - timestamp < DISMISS_DURATION_MS) {
        return; // Still within dismiss period
      } else {
        // Dismiss period expired, remove the key
        localStorage.removeItem(JOIN_MODAL_KEY);
      }
    }

    // Use setTimeout instead of interval to prevent continuous re-renders
    const timer = setTimeout(() => {
      if (isPageVisible) {
        setIsModalOpen(true);
        setHasShown(true);
        logAction('join_modal_impression' as any, { delay_seconds: 10 });
      }
    }, MODAL_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isAuthenticated, isHomePage, hasShown, isPageVisible, logAction]);

  const closeModal = () => {
    setIsModalOpen(false);
    // Set dismiss timestamp
    localStorage.setItem(JOIN_MODAL_KEY, JSON.stringify({
      timestamp: Date.now(),
      action: 'dismiss'
    }));
  };

  const handleCreateProfile = () => {
    setIsModalOpen(false);
    // Set permanent dismiss (no timestamp needed)
    localStorage.setItem(JOIN_MODAL_KEY, JSON.stringify({
      timestamp: Date.now(),
      action: 'create_profile',
      permanent: true
    }));
  };

  const handleBrowseFirst = () => {
    setIsModalOpen(false);
    // Set dismiss timestamp
    localStorage.setItem(JOIN_MODAL_KEY, JSON.stringify({
      timestamp: Date.now(),
      action: 'browse_first'
    }));
  };

  return {
    isModalOpen,
    closeModal,
    handleCreateProfile,
    handleBrowseFirst
  };
};